import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Card, Divider, Form, Image, Input, List, Modal, Select, Space, Spin, Tabs, Typography, message } from 'antd';
import { PlusOutlined, EditOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { listUserSeals, deleteUserSeal, generateEnterpriseSeal, generatePersonalSignature, getEnterpriseVerificationList } from '../../services/auth';
import { useBreakpoint } from '../../hooks/useBreakpoint';
import SignatureCanvas from '../../components/SignatureCanvas';
import './SealManagementPage.css';

/**
 * 印签管理页（个人中心）
 * 功能：
 * - 个人印签：用户手写签名 -> 系统生成标准化签名图片 -> 存储
 * - 企业公章：系统基于企业认证信息自动生成标准公章
 * - 所有印签均由系统生成，不允许用户上传图片（防止公章滥用）
 */
const SealManagementPage = () => {
  const { isMobile } = useBreakpoint();
  const { user } = useSelector(state => state.auth);
  const userId = user?.id;

  const [activeType, setActiveType] = useState('E');
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);

  // 企业公章生成相关状态
  const [generateOpen, setGenerateOpen] = useState(false);
  const [enterpriseList, setEnterpriseList] = useState([]);
  const [enterpriseListLoading, setEnterpriseListLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedEnterpriseId, setSelectedEnterpriseId] = useState(null);

  // 个人手写签名相关状态
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [signatureSaving, setSignatureSaving] = useState(false);
  const [signatureForm] = Form.useForm();

  const tabs = useMemo(() => ([
    { key: 'E', label: '企业公章' },
    { key: 'P', label: '个人印签' },
  ]), []);

  const formatTime = (t) => {
    if (!t) return '-';
    if (typeof t === 'string') {
      return t.replace('T', ' ').split('.')[0];
    }
    try {
      return new Date(t).toLocaleString();
    } catch {
      return String(t);
    }
  };

  const fetchList = async (sealType) => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data } = await listUserSeals(sealType);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      message.error(e?.message || '加载印签失败');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList(activeType);
  }, [activeType, userId]);

  // ==================== 企业公章生成相关 ====================

  const openGenerate = async () => {
    if (!userId) {
      message.error('用户未登录');
      return;
    }
    setGenerateOpen(true);
    setEnterpriseListLoading(true);
    setSelectedEnterpriseId(null);

    try {
      const { data } = await getEnterpriseVerificationList();
      const list = Array.isArray(data) ? data : [];

      if (list.length === 0) {
        message.warning('请先完成企业认证');
        setGenerateOpen(false);
        return;
      }

      setEnterpriseList(list);
    } catch (e) {
      message.error(e?.message || '获取企业认证信息失败');
      setGenerateOpen(false);
    } finally {
      setEnterpriseListLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedEnterpriseId) {
      message.error('请选择企业');
      return;
    }

    setGenerating(true);
    try {
      const selectedEnterprise = enterpriseList.find(e => e.id === selectedEnterpriseId);
      const sealName = `${selectedEnterprise?.companyName || '合同专用章'} (${selectedEnterprise?.socialCreditCode?.slice(-4) || ''})`;

      await generateEnterpriseSeal(selectedEnterpriseId, sealName);

      message.success('合同专用章生成成功');
      setGenerateOpen(false);
      fetchList('E');
    } catch (e) {
      message.error(e?.message || '生成合同专用章失败');
    } finally {
      setGenerating(false);
    }
  };

  // ==================== 个人手写签名相关 ====================

  const openSignature = () => {
    if (!userId) {
      message.error('用户未登录');
      return;
    }
    signatureForm.resetFields();
    signatureForm.setFieldsValue({ sealName: `${user?.realName || '个人'}的签名` });
    setSignatureOpen(true);
  };

  const handleSignatureConfirm = async (dataUrl) => {
    try {
      const values = await signatureForm.validateFields();
      setSignatureSaving(true);

      // 将Base64图片发送到后端生成个人签名
      await generatePersonalSignature({
        sealName: values.sealName,
        signatureData: dataUrl, // Base64格式图片数据
      });

      message.success('个人签名生成成功');
      setSignatureOpen(false);
      signatureForm.resetFields();
      fetchList('P');
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e?.message || '生成个人签名失败');
    } finally {
      setSignatureSaving(false);
    }
  };

  const handleSignatureCancel = () => {
    setSignatureOpen(false);
    signatureForm.resetFields();
  };

  const handleDelete = async (row) => {
    const id = row?.id;
    if (!id) return;
    Modal.confirm({
      title: '确认删除该印签？',
      content: '删除后将无法恢复。',
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        try {
          await deleteUserSeal(id);
          message.success('删除成功');
          fetchList(activeType);
        } catch (e) {
          message.error(e?.message || '删除失败');
        }
      },
    });
  };

  // ==================== 渲染相关 ====================

  const empty = (
    <div style={{ padding: '48px 0', textAlign: 'center' }} className="text-secondary">
      {activeType === 'E' ? '暂无企业公章，请点击"生成企业公章"自动生成' : '暂无个人印签，请点击"创建个人签名"添加'}
    </div>
  );

  return (
    <div className="page-container p-profile-seal-management">
      <div className="page-content page-content-wide space-y-md">
        <div className="p-profile-seal-management__titlebar">
          <div className="page-title">印签管理</div>
          <Space>
            {activeType === 'E' ? (
              <Button type="primary" icon={<PlusOutlined />} onClick={openGenerate} block={isMobile}>
                生成企业公章
              </Button>
            ) : (
              <Button type="primary" icon={<EditOutlined />} onClick={openSignature} block={isMobile}>
                创建个人签名
              </Button>
            )}
          </Space>
        </div>

        <Card
          className="card-compact"
          size="small"
          title={
            <Tabs
              activeKey={activeType}
              items={tabs}
              onChange={setActiveType}
              style={{ marginBottom: isMobile ? 0 : -12 }}
            />
          }
        >
          {loading ? (
            <div style={{ padding: 48, textAlign: 'center' }}>
              <Spin />
            </div>
          ) : (
            <List
              dataSource={items}
              locale={{ emptyText: empty }}
              grid={{ gutter: 16, xs: 1, sm: 2, md: 3, lg: 4, xl: 4, xxl: 5 }}
              renderItem={(row) => (
                <List.Item key={row?.id}>
                  <Card
                    size="small"
                    className="card-compact"
                    actions={[
                      <Button key="delete" type="link" danger onClick={() => handleDelete(row)}>删除</Button>
                    ]}
                  >
                    <Space direction="vertical" style={{ width: '100%' }} size={8}>
                      <div style={{ width: '100%', height: 140, display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#fafafa', borderRadius: 6 }}>
                        {row?.url ? (
                          <Image
                            src={row.url}
                            alt={row?.sealName || '印签'}
                            style={{ maxWidth: '100%', maxHeight: 132 }}
                          />
                        ) : (
                          <div style={{ color: '#999' }}>无图片</div>
                        )}
                      </div>
                      <Typography.Text ellipsis title={row?.sealName || ''}>
                        {row?.sealName || '-'}
                      </Typography.Text>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {row?.generationMethod === 'GENERATED' ? '生成时间：' : '上传时间：'}{formatTime(row?.uploadTime)}
                      </Typography.Text>
                    </Space>
                  </Card>
                </List.Item>
              )}
            />
          )}
        </Card>

        {/* 企业公章生成弹窗 */}
        <Modal
          title="生成企业公章"
          open={generateOpen}
          onCancel={() => setGenerateOpen(false)}
          onOk={handleGenerate}
          okText="生成"
          cancelText="取消"
          confirmLoading={generating}
          destroyOnClose
          width={isMobile ? 360 : 500}
        >
          {enterpriseListLoading ? (
            <div style={{ padding: 40, textAlign: 'center' }}>
              <Spin />
            </div>
          ) : (
            <Form layout="vertical">
              <Form.Item label="选择企业">
                <Select
                  placeholder="请选择已认证的企业"
                  value={selectedEnterpriseId}
                  onChange={setSelectedEnterpriseId}
                  options={enterpriseList.map(e => ({
                    label: `${e.companyName}（${e.socialCreditCode}）`,
                    value: e.id
                  }))}
                />
              </Form.Item>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                <CheckCircleOutlined /> 系统将基于企业认证信息自动生成标准公章，包含企业名称和防伪标识
              </Typography.Text>
            </Form>
          )}
        </Modal>

        {/* 个人手写签名弹窗 */}
        <Modal
          title="创建个人签名"
          open={signatureOpen}
          onCancel={handleSignatureCancel}
          footer={null}
          destroyOnClose
          width={isMobile ? 380 : 520}
        >
          <Form form={signatureForm} layout="vertical">
            <Form.Item
              name="sealName"
              label="签名名称"
              rules={[
                { required: true, message: '请输入签名名称' },
                { max: 50, message: '最多50个字符' },
              ]}
            >
              <Input placeholder="例如：张三的签名" />
            </Form.Item>

            <Divider style={{ margin: '12px 0' }}>手写签名</Divider>

            <SignatureCanvas
              onConfirm={handleSignatureConfirm}
              onCancel={handleSignatureCancel}
            />

            <div style={{ marginTop: 12, textAlign: 'center' }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                <CheckCircleOutlined /> 签名将生成标准化图片，用于合同签署
              </Typography.Text>
            </div>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default SealManagementPage;
